export type MessageTextSegment =
  | { kind: 'text'; value: string }
  | { kind: 'url'; value: string; href: string }
  | { kind: 'mention'; value: string; uid: string };

type Candidate = Exclude<MessageTextSegment, { kind: 'text' }> & {
  start: number;
  end: number;
};

const URL_RE = /(?:https?:\/\/|www\.)[^\s<>"']+/giu;
const TRAILING_URL_PUNCTUATION_RE = /[.,;:!?)\]}]+$/u;

function validWebUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:')
      && Boolean(parsed.hostname)
      // Userinfo, refused the same way messageLinkHref refuses it
      // (netlify/functions/lib/messageFormatting.mjs). `https://ldg-hub.com@evil.example/`
      // reads to a parent as the studio's own address and goes somewhere else,
      // and the marked-link path already turns it down. The plain-text
      // linkifier is the same door and must give the same answer.
      && !parsed.username
      && !parsed.password;
  } catch {
    return false;
  }
}

/**
 * Split message text into safe render tokens. Only explicit http(s) URLs become
 * links; everything else remains React text, so pasted markup cannot become
 * executable HTML. Mention chips retain the message's server-provided uid list.
 *
 * `aliasHandleByUid` is optional and additive (Codex round 8 of #796,
 * finding 3): a legacy thread's OLD, pre-round-6 handle form for a
 * mentioned uid whose CURRENT handle has since changed
 * (threadLegacyMentionAliases, lib/threadLabels.ts). Without it, a message
 * whose `mentions` array correctly still names the uid stops rendering the
 * chip the moment the room's derived handle changes underneath it, because
 * the OLD text still reads "@Mia" while the regex here only knows
 * "@MiaFord". Passing both handle forms for the same uid lets either
 * spelling in the raw text resolve to the one right uid.
 */
export function messageTextSegments(
  text: string,
  mentions: string[],
  handleByUid: Map<string, string>,
  aliasHandleByUid?: ReadonlyMap<string, string>
): MessageTextSegment[] {
  const candidates: Candidate[] = [];

  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    const value = match[0].replace(TRAILING_URL_PUNCTUATION_RE, '');
    const href = /^www\./i.test(value) ? `https://${value}` : value;
    if (!value || !validWebUrl(href)) continue;
    if (/^www\./i.test(value) && start > 0 && /[\w@.]/u.test(text[start - 1])) continue;
    candidates.push({ kind: 'url', start, end: start + value.length, value, href });
  }

  const handles = mentions
    .flatMap((uid) => [
      { uid, handle: handleByUid.get(uid) },
      { uid, handle: aliasHandleByUid?.get(uid) },
    ])
    .filter((entry): entry is { uid: string; handle: string } => Boolean(entry.handle))
    .sort((a, b) => b.handle.length - a.handle.length);
  if (handles.length > 0) {
    const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const mentionRe = new RegExp(`@(${handles.map(({ handle }) => escape(handle)).join('|')})\\b`, 'g');
    const uidByHandle = new Map(handles.map(({ uid, handle }) => [handle, uid]));
    for (const match of text.matchAll(mentionRe)) {
      const start = match.index ?? 0;
      const uid = uidByHandle.get(match[1]);
      if (!uid) continue;
      candidates.push({
        kind: 'mention', start, end: start + match[0].length,
        value: match[0], uid,
      });
    }
  }

  candidates.sort((a, b) => a.start - b.start || b.end - a.end);
  const segments: MessageTextSegment[] = [];
  let cursor = 0;
  for (const candidate of candidates) {
    if (candidate.start < cursor) continue;
    if (candidate.start > cursor) {
      segments.push({ kind: 'text', value: text.slice(cursor, candidate.start) });
    }
    if (candidate.kind === 'url') {
      segments.push({ kind: 'url', value: candidate.value, href: candidate.href });
    } else {
      segments.push({ kind: 'mention', value: candidate.value, uid: candidate.uid });
    }
    cursor = candidate.end;
  }
  if (cursor < text.length) segments.push({ kind: 'text', value: text.slice(cursor) });
  return segments.length > 0 ? segments : [{ kind: 'text', value: text }];
}
